import { useState } from "react";
import { BellRing, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function NotifyMe({ kind, itemId, itemName, label = "Notify Me When Available", className = "", triggerTestId }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/notify/signup", { kind, item_id: itemId, email });
      setDone(true);
      toast.success(data.already ? "You're already on the list" : "You're on the list — we'll email you first");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sign you up");
    } finally { setBusy(false); }
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const openDialog = (e) => {
    // Prevent parent <Link>/<a> navigation AND open dialog imperatively
    // (using DialogTrigger asChild + preventDefault causes Radix to bail).
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button onClick={openDialog} type="button" data-testid={triggerTestId || `notify-btn-${itemId}`}
          className={className || "inline-flex items-center gap-2 border border-eon-gold text-eon-gold text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-eon-gold hover:text-black transition-colors"}>
          <BellRing size={14} /> {label}
        </button>
      <DialogContent onClick={(e) => e.stopPropagation()} className="bg-eon-surface border-eon-border text-eon-text">
        <DialogHeader><DialogTitle className="font-display text-2xl">{kind === "dog" ? "Join the Waitlist" : "Notify Me"}</DialogTitle>
          <DialogDescription className="text-eon-muted">{kind === "dog" ? "First-in-line access when this puppy is available." : "We'll email you the moment it's available."}</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="notify-success">
            <CheckCircle2 size={40} className="text-eon-gold mx-auto" />
            <p className="mt-4 text-eon-text/80">You're first in line for <span className="text-eon-gold">{itemName}</span>. We'll email you the moment it's available.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2" data-testid="notify-form">
            <p className="text-sm text-eon-muted">{kind === "dog"
              ? `Get first-in-line access when ${itemName} becomes available to reserve.`
              : `We'll email you the moment ${itemName} is back and ready to order.`}</p>
            <input required type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-eon-bg border border-eon-border px-3 py-3 focus:border-eon-gold outline-none" data-testid="notify-email" />
            <button type="submit" disabled={busy} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3.5 hover:bg-eon-goldhover transition-colors disabled:opacity-40" data-testid="notify-submit">
              {busy ? "Adding..." : kind === "dog" ? "Join Waitlist" : "Notify Me"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
